import type { ReactNode } from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';
import { useT } from '@/i18n';
import { PoweredBy, Wordmark } from '@/components/ui';
import { isTenantBrand, useBranding } from '@/lib/branding';
import { LanguageSwitch } from '@/layout/AppShell';
import { setThemePref } from '@/lib/prefs';
import { nextTheme, usePrefs } from '@/store/prefs';

export function AuthLayout({ title, sub, children, wide }: { title: string; sub?: ReactNode; children: ReactNode; wide?: boolean }) {
  const { t } = useT();
  const theme = usePrefs((s) => s.theme);
  const Icon = theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor;
  const mode = t(theme === 'dark' ? 'themeDark' : theme === 'light' ? 'themeLight' : 'themeSystem');
  return (
    <div className="auth">
      <div className="auth-top">
        <button type="button" className="btn btn-icon" aria-label={t('themeTip', { mode })} title={t('themeTip', { mode })} onClick={() => void setThemePref(nextTheme(theme))}>
          <Icon size={16} aria-hidden />
        </button>
        <LanguageSwitch />
      </div>
      <main className="auth-card" style={wide ? { width: 'min(520px, 100%)' } : undefined}>
        <Wordmark size={20} />
        <div className="stack">
          <h1 className="h1">{title}</h1>
          {sub ? <p className="muted" style={{ margin: 0 }}>{sub}</p> : null}
        </div>
        {children}
      </main>
      <BrandFooter />
    </div>
  );
}

/** On a tenant's domain: its help, terms and privacy links, and "Powered by Castfeeds" unless removed. */
export function BrandFooter() {
  const { t } = useT();
  const b = useBranding((s) => s.branding);
  if (!isTenantBrand(b)) return null;
  const links = ([['help', t('wlHelp')], ['terms', t('wlTerms')], ['privacy', t('wlPrivacy')]] as const).filter(([k]) => b.links[k]);
  if (!links.length && !b.powered_by) return null;
  return (
    <footer className="row wrap small muted" style={{ justifyContent: 'center', gap: 14, padding: '14px 0' }}>
      {links.map(([k, label]) => (
        <a key={k} href={b.links[k]!} target="_blank" rel="noreferrer">{label}</a>
      ))}
      <PoweredBy />
    </footer>
  );
}
